// Web Register pricing engine — barrel + recalculate orchestrator.
// Pure JS, no React/Redux. The cart slice calls recalculate() on every cart mutation.

import { toNum, roundHalfUp } from "./money";
import { calculateSubtotal } from "./subtotal";
import { calculatePennyDifference } from "./penny";
import { calculateTaxes, calculateFoodStampable } from "./tax";
import { calculateGrandTotal } from "./total";
import { applyBogoDeals } from "./bogo";
import { validateBulkPrice } from "./bulkPrice";
import { applyDeziItemDiscounts, deziUpcSetOf, markDeziUpcLines } from "./dezi";
import { toBasePrice, computeDualTotals, isDualPrice } from "./dualPrice";

export * from "./money";
export * from "./subtotal";
export * from "./penny";
export * from "./tax";
export * from "./total";
export * from "./bogo";
export * from "./bulkPrice";
export * from "./itemSession";
export * from "./savePayload";
export * from "./normalizeProduct";
export * from "./itemCoupon";
export * from "./refund";
export * from "./orderDetail";
export * from "./dezi";
export * from "./promoHelpers";
export * from "./lotteryPayout";
export * from "./dualPrice";

// Reset each line's working price to its base before re-applying promotions, so recalculate()
// is idempotent. manualOverridePrice (0 unless a cashier overrode the price) is preserved;
// bulk/bogo state is cleared and recomputed. orderUnitPrice stays the immutable catalog price.
//
// ⚠️ RESTORE-PRESERVED BOGO SETS ARE EXCLUDED. A restored (`isRestoredBogo`) set must keep the price
// and identifier the backend gave it — that is what preserves per-line tax on a held sale — until the
// cashier edits the line. This mirrors Android's own reset filter; the ONLY thing that can un-preserve
// such a set is `applyBogoDeals`' broken-group unfreeze.
// (The Design-2 commit-lock, `isCommittedBogo`, was retired with Android's 2026-08-08 add-order
//  membership rework: freezing is now structural, so nothing needs to survive a recalculation.)
function resetWorkingPrices(cart) {
  for (const l of cart) {
    // Dezi is re-derived from the held mKonnekt result on every pass, so the previous contribution is
    // always stripped first — this is what makes double-applying a manufacturer discount impossible.
    //
    // ⚠️ Removing it from `orderDiscount` is the load-bearing half (Android `clearItemDiscounts`).
    // `orderDiscount` is the COMBINED per-unit discount the money engine reads — manual + item coupon
    // + manufacturer — and it is only rebuilt when the manual/coupon fields change, NOT on every
    // recalculation. Zeroing `deziItemDiscountAmt` alone would leave the old amount inside
    // `orderDiscount` and the injector would add a second one on top of it. Floored at 0 and keyed on
    // the recorded amount, so a manual discount on a non-dezi line is never touched.
    const prevDezi = toNum(l.deziItemDiscountAmt);
    if (prevDezi > 0) l.orderDiscount = Math.max(toNum(l.orderDiscount) - prevDezi, 0);
    l.deziItemDiscountAmt = 0;
    l.deziBreakdown = [];
    if (l.isRestoredBogo) continue;
    if (l.catalogUnitPrice != null) l.orderUnitPrice = toNum(l.catalogUnitPrice); // restore base (BOGO overwrites it)
    l.orderUnitNewPrice = toNum(l.manualOverridePrice);
    l.isBulkPriceApplied = false;
    l.isBoGoProduct = false;
    l.bogoIdentifier = 0;
    l.bogoDealId = null;
  }
}

/**
 * Run the full pricing pipeline over a cart and return derived totals.
 * @param {object} s
 *  - cart: cart line array (mutated in place: jsonNewPrice, discounts, tax buckets, promo flags)
 *  - bogoList: Go `bogos` bundle
 *  - now: Date (store timezone) for BOGO schedule
 *  - orderPercentDiscount / orderAmountDiscount: $ value of a %/$ order-level discount
 *  - couponTotal / customTotal: coupon-type vs custom-type order discount $ (for the item_ses split)
 *  - loyaltyAmount / storeCredit / giftCard: reductions (Phase 2; pass 0 in v1)
 */
export function recalculate(s = {}) {
  const {
    cart = [],
    bogoList = [],
    now = new Date(),
    orderPercentDiscount = 0,
    orderAmountDiscount = 0,
    couponTotal = 0,
    customTotal = 0,
    loyaltyAmount = 0,
    storeCredit = 0,
    giftCard = 0,
    isOtdCoupon = false, // OTD keeps the full-cart coupon spread (exclude_coupons_store)
    couponDollarDiscount, // Σ $ lines that are LIST coupons (stacked discounts) — undefined = legacy XOR
    percentIsRealCoupon, // the % line is a LIST coupon — undefined = legacy XOR
    deziDiscounts = null, // QV-7124: the held mKonnekt get_discounts result (null = feature off/no result)
    nextLineId = null, // used only when a Mix&Match cap splits a straddling line
    dualPrice = null, // QV-4241 dualPriceConfig — null/mode "0" ⇒ inert
    isSplit = false, // split payment in progress (Android isSplitPayment) — card columns stop deriving from base
  } = s;

  // QV-7124 — the upcs carrying a live manufacturer discount. Drives every suppression gate below and
  // is empty for a non-dezi store, so each gate is a no-op and non-dezi carts stay byte-identical.
  const deziUpcSet = deziUpcSetOf(deziDiscounts);

  resetWorkingPrices(cart);
  // ⚠️ THE ORDER BELOW IS LOAD-BEARING (Android applyDeziDiscounts steps d→f). Deals run FIRST with
  // dezi upcs excluded, so every dezi line sits at its RAW price by the time the injector measures
  // headroom; injecting first would cap the manufacturer discount against a deal-reduced price.
  validateBulkPrice(cart, deziUpcSet, nextLineId);
  applyBogoDeals(cart, bogoList, now, deziUpcSet);
  applyDeziItemDiscounts(cart, deziDiscounts);
  markDeziUpcLines(cart, deziUpcSet);

  const st = calculateSubtotal(cart);
  calculatePennyDifference(cart, {
    mSubTotal: st.subtotal,
    orderPercentDiscount,
    orderAmountDiscount,
    couponTotal,
    customTotal,
    loyaltyAmount,
    isOtdCoupon,
    couponDollarDiscount,
    percentIsRealCoupon,
    deziUpcSet, // rule 4: dezi lines are excluded from the order-level distribution
    isDual: isDualPrice(dualPrice),
    toBase: (v) => toBasePrice(dualPrice, v),
  });
  const tax = calculateTaxes(cart, { toBase: (v) => toBasePrice(dualPrice, v) });
  // Android getTaxRates: orderDiscountPercentage = couponDiscountValue ÷ (subtotal − lottery buys), NaN → 0.
  const lotterySubtotal = st.subtotal - (st.lotteryBuyAmount || 0);
  const orderDiscountPercentage = lotterySubtotal > 0 ? roundHalfUp(couponTotal + customTotal) / lotterySubtotal : 0;
  const totalFoodStampable = calculateFoodStampable(cart, tax, orderDiscountPercentage);
  const lotteryScratcher = st.lotteryPayoutAmount + st.scratcherPayoutAmount;
  let grandTotal = calculateGrandTotal({
    subtotal: st.subtotal,
    tax: tax.taxRateInAmount,
    couponPlusCustom: roundHalfUp(couponTotal + customTotal),
    storeCredit,
    lotteryScratcher,
    giftCard,
    loyalty: loyaltyAmount,
  });

  const dualInput = {
    lines: cart,
    subtotal: st.subtotal,
    couponDiscountValue: roundHalfUp(couponTotal + customTotal),
    taxRateInAmount: tax.taxRateInAmount,
    baseTaxRateInAmount: tax.baseTaxRateInAmount,
    grandTotal,
    loyalty: loyaltyAmount,
    storeCredit,
    lotteryScratcher,
    redeemedGiftCard: giftCard,
    isSplit,
    taxLines: Array.from(tax.taxByName.values()).map((t) => ({ name: t.name, amount: roundHalfUp(t.price) })),
  };
  // Android calculateOrder wallet block (dual only): wallets come off at FACE; when SC + redeemed gift
  // card + points reach the CASH goods total the whole card premium is waived, else each fully
  // points-paid line waives its own premium.
  const pointsPaidNcaWaived = roundHalfUp(cart.reduce((s2, l) => s2 + toNum(l.pointsPaidNcaWaived), 0));
  if (isDualPrice(dualPrice)) {
    const preWallet = roundHalfUp(st.subtotal + tax.taxRateInAmount - (toNum(couponTotal) + toNum(customTotal) > 0 ? roundHalfUp(couponTotal + customTotal) : 0));
    if (preWallet > 0) {
      const d0 = computeDualTotals(dualPrice, dualInput);
      const target = roundHalfUp(d0.baseSubTotal - d0.baseOrderDiscount + d0.baseTax);
      if (roundHalfUp(toNum(storeCredit) + toNum(giftCard) + toNum(loyaltyAmount)) >= target) {
        grandTotal = roundHalfUp(Math.max(grandTotal - roundHalfUp(preWallet - target), 0));
      } else if (pointsPaidNcaWaived > 0) {
        grandTotal = roundHalfUp(Math.max(grandTotal - pointsPaidNcaWaived, 0));
      }
      dualInput.grandTotal = grandTotal;
    }
  }
  const dual = computeDualTotals(dualPrice, dualInput);

  return {
    cart,
    dual,
    pointsPaidNcaWaived,
    subtotal: st.subtotal,
    tax,
    taxRateInAmount: tax.taxRateInAmount,
    totalFoodStampable, // Android cart summary "Food Stampable" row (shown when ≠ 0)
    grandTotal,
    lotteryScratcher, // Σ lottery + scratcher payout lines (subtracted inside grandTotal; re-added into `amt`)
    lotteryBuyAmount: st.lotteryBuyAmount,
    lotteryPayoutAmount: st.lotteryPayoutAmount,
    scratcherPayoutAmount: st.scratcherPayoutAmount,
  };
}
